/**
 * Dashboard Overview Page (Korat Health Style)
 *
 * Redesigned with Sidebar, Information-rich KPI cards, and Pastel category cards
 */

import { useState } from 'react'
import { Users, Building2, Wallet, Heart, Accessibility, UserCheck, Cross, Shield, AlertTriangle, AlertCircle, Info, Sparkles } from 'lucide-react'
import { Sidebar, PageHeader, KpiCardRich, SubCard } from '../shared/components'
import { PieChartCard, BarChartCard } from '../shared/components/charts'
import {
  usePersonnelOverview,
  useFinancialOverview,
  useVulnerableOverview,
  usePopulationStructure,
} from '../services/api'

export function DashboardKorat() {
  const [selectedYear] = useState(2567)

  // Fetch all module data
  const { data: personnelData, isLoading: personnelLoading } = usePersonnelOverview(selectedYear)
  const { data: financialData, isLoading: financialLoading } = useFinancialOverview(selectedYear)
  const { data: vulnerableData, isLoading: vulnerableLoading } = useVulnerableOverview(selectedYear)
  const { data: populationData, isLoading: populationLoading } = usePopulationStructure(selectedYear)

  const formatNumber = (value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value
    if (num >= 1_000_000_000) {
      return `${(num / 1_000_000_000).toFixed(1)}B`
    }
    if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(1)}M`
    }
    if (num >= 1_000) {
      return `${(num / 1_000).toFixed(1)}K`
    }
    return new Intl.NumberFormat('th-TH').format(num)
  }


  // Calculate population buckets percentages
  const childrenPercent = populationData?.buckets.find(b => b.bucket === 'children')
    ? ((parseFloat(populationData.buckets.find(b => b.bucket === 'children')!.total) / parseFloat(populationData.totals.grand_total)) * 100).toFixed(1)
    : '0'
  const workingPercent = populationData?.buckets.find(b => b.bucket === 'working_age')
    ? ((parseFloat(populationData.buckets.find(b => b.bucket === 'working_age')!.total) / parseFloat(populationData.totals.grand_total)) * 100).toFixed(1)
    : '0'
  const elderlyPercent = populationData?.buckets.find(b => b.bucket === 'elderly')
    ? ((parseFloat(populationData.buckets.find(b => b.bucket === 'elderly')!.total) / parseFloat(populationData.totals.grand_total)) * 100).toFixed(1)
    : '0'

  // Calculate budget utilization
  const expenseSummary = financialData?.summary.find(s => s.category_type === 'expense')
  const totalBudget = expenseSummary ? parseFloat(expenseSummary.total_budget) : 0
  const totalSpent = expenseSummary ? parseFloat(expenseSummary.total_spent) : 0
  const remaining = totalBudget - totalSpent
  const utilization = expenseSummary ? parseFloat(expenseSummary.avg_utilization) : 0

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <Sidebar currentPage="dashboard" />

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Page Header */}
        <PageHeader
          icon={Heart}
          title="ข้อมูลสุขภาพจังหวัดนครราชสีมา"
          showFilters={true}
        />

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* KPI Section - Korat Brand Colors (Navy + Orange) */}
          <section>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Population Structure - NAVY PRIMARY */}
              <KpiCardRich
                icon={Users}
                title="โครงสร้างประชากร"
                description="สรุปจำนวนประชากร เพศ ช่วงวัย และกลุ่มที่ต้องดูแลพิเศษในพื้นที่"
                value={populationData?.totals.grand_total || 0}
                unit="คน"
                helperText="ใช้เป็นฐานในการคำนวณสัดส่วนเพศและช่วงวัย"
                breakdown={[
                  `<b>เด็ก</b> ${childrenPercent}%`,
                  `<b>วัยทำงาน</b> ${workingPercent}%`,
                  `<b>ผู้สูงอายุ</b> ${elderlyPercent}%`,
                ]}
                isLoading={populationLoading}
                variant="navy"
              />

              {/* Personnel - ORANGE ACCENT */}
              <KpiCardRich
                icon={Building2}
                title="บุคลากรสาธารณสุข"
                description="จำนวนบุคลากรทางการแพทย์และสนับสนุนทุกประเภท"
                value={personnelData?.totals.grand_total || 0}
                unit="คน"
                helperText={`เฉลี่ย ${personnelData ? ((parseFloat(personnelData.totals.grand_total) / parseFloat(populationData?.totals.grand_total || '1')) * 10000).toFixed(1) : '0'} ต่อหมื่นคน`}
                breakdown={[
                  `<b>แพทย์</b> ${formatNumber(personnelData?.by_category.find(c => c.abbreviation === 'MD')?.total_staff || 0)}`,
                  `<b>พยาบาล</b> ${formatNumber(personnelData?.by_category.find(c => c.abbreviation === 'RN')?.total_staff || 0)}`,
                  `<b>เภสัชกร</b> ${formatNumber(personnelData?.by_category.find(c => c.abbreviation === 'PharmD')?.total_staff || 0)}`,
                ]}
                isLoading={personnelLoading}
                variant="orange"
              />

              {/* Financial - NAVY SECONDARY */}
              <KpiCardRich
                icon={Wallet}
                title="งบประมาณและการเงิน"
                description="สรุปงบรายรับ รายจ่าย และอัตราการใช้จ่ายในปีงบประมาณปัจจุบัน"
                value={totalBudget}
                unit="บาท"
                helperText="งบรายจ่ายทั้งหมด"
                breakdown={[
                  `<b>รายรับ</b> ${formatNumber(financialData?.summary.find(s => s.category_type === 'income')?.total_budget || 0)}`,
                  `<b>รายจ่าย</b> ${formatNumber(totalSpent)}`,
                  `<b>คงเหลือ</b> ${formatNumber(remaining)}`,
                  `<b>ใช้ไป</b> ${utilization.toFixed(1)}%`,
                ]}
                isLoading={financialLoading}
                variant="blue"
              />

              {/* Vulnerable Populations - ORANGE SECONDARY */}
              <KpiCardRich
                icon={Heart}
                title="กลุ่มเปราะบาง"
                description="กลุ่มประชากรที่ต้องการดูแลเป็นพิเศษและสถานะการให้การช่วยเหลือ"
                value={vulnerableData?.totals.grand_total || 0}
                unit="คน"
                helperText={`คิดเป็น ${populationData ? ((parseFloat(vulnerableData?.totals.grand_total || '0') / parseFloat(populationData.totals.grand_total)) * 100).toFixed(2) : '0'}% ของประชากร`}
                breakdown={[
                  `<b>ต้องดูแลเร่งด่วน</b> ${formatNumber(vulnerableData?.totals.total_immediate_care || 0)}`,
                  `<b>กำลังได้รับ</b> ${formatNumber(vulnerableData?.totals.total_receiving_support || 0)}`,
                ]}
                isLoading={vulnerableLoading}
                variant="orange"
              />
            </div>
          </section>

          {/* Vulnerable Groups Detail - Korat Orange Accent */}
          <section>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-korat-navy-700">
                กลุ่มประชากรที่ต้องการดูแลเป็นพิเศษ
              </h2>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                รายละเอียดกลุ่มเปราะบางตามประเภทและความต้องการในพื้นที่
              </p>
            </div>

            {/* Main Container Card - Orange Border */}
            <div className="bg-white rounded-xl border-2 border-korat-orange-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-6">
              {/* Header - Orange Theme */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-korat-orange-100 flex items-center justify-center shrink-0">
                  <Heart className="w-5 h-5 text-korat-orange-600" aria-hidden />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-korat-navy-700 leading-tight">
                    กลุ่มเปราะบางทั้งหมด
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
                    ประชากรที่ต้องการการดูแลและช่วยเหลือเป็นพิเศษในพื้นที่ จำแนกตามประเภทความเสี่ยงและความต้องการ
                  </p>
                </div>
              </div>

              {/* Big Number - Navy */}
              {vulnerableLoading ? (
                <div className="h-10 bg-slate-200 rounded animate-pulse mb-1" />
              ) : (
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-korat-navy-700 tabular-nums">
                    {formatNumber(vulnerableData?.totals.grand_total || 0)}
                  </span>
                  <span className="text-sm text-slate-500">คน</span>
                </div>
              )}

              {/* Helper Text */}
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                คิดเป็น {populationData ? ((parseFloat(vulnerableData?.totals.grand_total || '0') / parseFloat(populationData.totals.grand_total)) * 100).toFixed(2) : '0'}% ของประชากรทั้งหมด
              </p>

              {/* Divider - Orange tint */}
              <div className="my-4 border-t border-korat-orange-100" />

              {/* Sub-cards Grid (2x2) - Navy variants */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Disabled - Navy */}
                <SubCard
                  icon={Accessibility}
                  title="ผู้พิการ"
                  description="ต้องการดูแลช่วยเหลือพิเศษ"
                  value={vulnerableData?.by_group.find(g => g.name_en === 'Persons with Disabilities')?.total_population || 0}
                  unit="คน"
                  meta={`${populationData ? ((parseFloat(vulnerableData?.by_group.find(g => g.name_en === 'Persons with Disabilities')?.total_population || '0') / parseFloat(populationData.totals.grand_total)) * 100).toFixed(2) : '0'}% • ต้องดูแล ${formatNumber(vulnerableData?.by_group.find(g => g.name_en === 'Persons with Disabilities')?.total_immediate_care || 0)} รายเร่งด่วน`}
                  variant="slate"
                  isLoading={vulnerableLoading}
                />

                {/* Low Income - Orange */}
                <SubCard
                  icon={UserCheck}
                  title="ผู้มีรายได้น้อย"
                  description="เสี่ยงด้านสุขภาพ"
                  value={vulnerableData?.by_group.find(g => g.name_en === 'Low-Income Individuals')?.total_population || 0}
                  unit="คน"
                  meta={`${populationData ? ((parseFloat(vulnerableData?.by_group.find(g => g.name_en === 'Low-Income Individuals')?.total_population || '0') / parseFloat(populationData.totals.grand_total)) * 100).toFixed(2) : '0'}% • ช่วยเหลือ ${formatNumber(vulnerableData?.by_group.find(g => g.name_en === 'Low-Income Individuals')?.total_receiving_support || 0)} ครัวเรือน`}
                  variant="orange"
                  isLoading={vulnerableLoading}
                />

                {/* Monks - Slate */}
                <SubCard
                  icon={Cross}
                  title="พระสงฆ์"
                  description="พระสงฆ์ในพื้นที่ ต้องการติดตาม"
                  value={vulnerableData?.by_group.find(g => g.name_en === 'Buddhist Monks')?.total_population || 0}
                  unit="รูป"
                  meta={`${populationData ? ((parseFloat(vulnerableData?.by_group.find(g => g.name_en === 'Buddhist Monks')?.total_population || '0') / parseFloat(populationData.totals.grand_total)) * 100).toFixed(2) : '0'}% • ตรวจสุขภาพประจำ`}
                  variant="slate"
                  isLoading={vulnerableLoading}
                />

                {/* Incarcerated - Orange */}
                <SubCard
                  icon={Shield}
                  title="ผู้ต้องขัง"
                  description="ในสถานกักกัน ต้องการบริการสุขภาพ"
                  value={vulnerableData?.by_group.find(g => g.name_en === 'Incarcerated Persons')?.total_population || 0}
                  unit="คน"
                  meta={`${populationData ? ((parseFloat(vulnerableData?.by_group.find(g => g.name_en === 'Incarcerated Persons')?.total_population || '0') / parseFloat(populationData.totals.grand_total)) * 100).toFixed(2) : '0'}% • ตรวจประจำ 2 เรือนจำ`}
                  variant="orange"
                  isLoading={vulnerableLoading}
                />
              </div>
            </div>
          </section>

          {/* Data Visualization - Charts Section */}
          <section>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-korat-navy-700">
                ภาพรวมข้อมูลเชิงลึก
              </h2>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                การวิเคราะห์ข้อมูลสำคัญในรูปแบบ Visual
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Age Distribution Pie Chart */}
              <PieChartCard
                key={populationData ? 'pop-loaded' : 'pop-loading'}
                title="โครงสร้างอายุประชากร"
                description="สัดส่วนประชากรจำแนกตามช่วงอายุ"
                data={
                  populationData?.buckets
                    ? [
                        {
                          name: 'เด็กและเยาวชน (0-18)',
                          value: parseFloat(
                            populationData.buckets.find((b) => b.bucket === 'children')?.total || '0'
                          ),
                        },
                        {
                          name: 'วัยทำงาน (19-59)',
                          value: parseFloat(
                            populationData.buckets.find((b) => b.bucket === 'working_age')?.total || '0'
                          ),
                        },
                        {
                          name: 'ผู้สูงอายุ (60+)',
                          value: parseFloat(
                            populationData.buckets.find((b) => b.bucket === 'elderly')?.total || '0'
                          ),
                        },
                      ]
                    : []
                }
                colorScheme="primary"
                isLoading={populationLoading}
              />

              {/* Vulnerable Groups Bar Chart */}
              <BarChartCard
                key={vulnerableData ? 'vuln-loaded' : 'vuln-loading'}
                title="กลุ่มเปราะบางตามประเภท"
                description="จำนวนประชากรในแต่ละกลุ่มเสี่ยง"
                data={
                  vulnerableData?.by_group
                    ? vulnerableData.by_group
                        .sort((a, b) => parseFloat(b.total_population) - parseFloat(a.total_population))
                        .map((group) => ({
                          name: group.name_th,
                          value: parseFloat(group.total_population),
                        }))
                    : []
                }
                color="navy"
                isLoading={vulnerableLoading}
              />

              {/* Budget Breakdown Bar Chart */}
              <BarChartCard
                key={financialData ? 'fin-loaded' : 'fin-loading'}
                title="งบประมาณตามหมวดหมู่"
                description="การจัดสรรงบประมาณรายหมวด (บาท)"
                data={
                  financialData?.by_category
                    ? financialData.by_category
                        .filter((c) => c.category_type === 'expense')
                        .sort((a, b) => parseFloat(b.budget_allocated) - parseFloat(a.budget_allocated))
                        .slice(0, 5)
                        .map((category) => ({
                          name: category.name_th,
                          value: parseFloat(category.budget_allocated),
                        }))
                    : []
                }
                color="blue"
                horizontal={true}
                isLoading={financialLoading}
              />

              {/* Personnel Distribution Bar Chart */}
              <BarChartCard
                key={personnelData ? 'pers-loaded' : 'pers-loading'}
                title="สัดส่วนบุคลากรสาธารณสุข"
                description="จำนวนบุคลากรตามประเภท"
                data={
                  personnelData?.by_category
                    ? personnelData.by_category
                        .sort((a, b) => parseFloat(String(b.total_staff)) - parseFloat(String(a.total_staff)))
                        .slice(0, 5) // Top 5 categories
                        .map((category) => ({
                          name: category.name_th,
                          value: parseFloat(String(category.total_staff)),
                        }))
                    : []
                }
                color="orange"
                horizontal={true}
                isLoading={personnelLoading}
              />
            </div>
          </section>

          {/* Alert Section - Korat Brand Colors */}
          <section>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-korat-navy-700">
                การแจ้งเตือนสำคัญ
              </h2>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                สถานการณ์ที่ต้องเฝ้าระวังและดำเนินการ
              </p>
            </div>

            <div className="space-y-3">
              {/* Alert 1: High Risk - Korat Orange */}
              <div className="rounded-lg border-l-4 border-korat-orange-500 bg-korat-orange-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-korat-orange-700 shrink-0" aria-hidden />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-korat-orange-700">
                      High risk: เมืองนครราชสีมา (HIGH)
                    </h3>
                    <p className="text-sm text-korat-orange-700/80 mt-0.5">
                      พบผู้ป่วยโรคติดต่อในระดับสูง
                    </p>
                  </div>
                </div>
              </div>

              {/* Alert 2: Warning - Korat Orange (lighter) */}
              <div className="rounded-lg border-l-4 border-korat-orange-600 bg-korat-orange-50/50 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-korat-orange-600 shrink-0" aria-hidden />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-korat-orange-700">
                      Vulnerable: 4.68% ประชากรพิการ
                    </h3>
                    <p className="text-sm text-korat-orange-700/80 mt-0.5">
                      สูงกว่าค่าเฉลี่ยประเทศ
                    </p>
                  </div>
                </div>
              </div>

              {/* Alert 3: Info - Korat Navy */}
              <div className="rounded-lg border-l-4 border-korat-navy-500 bg-korat-navy-50 p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-korat-navy-700 shrink-0" aria-hidden />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-korat-navy-700">
                      Budget: 91% อัตราการใช้งบ
                    </h3>
                    <p className="text-sm text-korat-navy-700/80 mt-0.5">
                      ใกล้ถึงเป้าหมาย 95%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Module Navigation - Korat Brand Gradient */}
          <section>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-korat-navy-700">
                โมดูลข้อมูลเพิ่มเติม
              </h2>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                เลือกโมดูลเพื่อดูข้อมูลรายละเอียด
              </p>
            </div>

            <div className="bg-gradient-to-r from-korat-navy-50 to-korat-orange-50 rounded-xl border-2 border-korat-navy-100 p-8 text-center">
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5 text-korat-orange-500" aria-hidden />
                <p className="text-sm font-medium text-korat-navy-700">
                  โมดูลอื่นๆ จะปรับปรุงในเร็วๆ นี้
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}
